import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  CheckCircle,
  Cancel,
  MoreVert,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { chargersService } from '../services/api';
import { ChargerUnit } from '../types';
import { useAuth } from '../contexts/AuthContext';
import AddChargerRequestModal from '../components/AddChargerRequestModal';


const Chargers: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const [chargers, setChargers] = useState<ChargerUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isAddChargerModalOpen, setIsAddChargerModalOpen] = useState(false);

  // Fetch chargers from API
  useEffect(() => {
    const fetchChargers = async () => {
      if (!user || !user.orgId) {
        console.warn('User or orgId not available:', { user, orgId: user?.orgId });
        setError('Organization ID not found');
        setLoading(false);
        setChargers([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setErrorCode(null);
        console.log('Fetching chargers for orgId:', user.orgId);
        const response = await chargersService.getChargers(user.orgId);
        console.log('Chargers API response:', response);
        
        if (response.success && response.data) {
          // Safely access units array, default to empty array if not present
          const units = Array.isArray(response.data.units) ? response.data.units : [];
          console.log('Setting chargers, count:', units.length);
          setChargers(units);
        } else {
          console.warn('Chargers API response not successful:', response);
          setError('Failed to fetch chargers');
          setChargers([]); // Set empty array to show empty table
        }
      } catch (err) {
        console.error('Error fetching chargers:', err);
        setChargers([]); // Set empty array to show empty table
        if (err instanceof Error) {
          // Extract error code from error message if available
          const match = err.message.match(/status: (\d+)/);
          if (match) {
            setErrorCode(match[1]);
          }
          setError(err.message);
        } else {
          setError('Failed to fetch chargers');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchChargers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.orgId]);


  const getAvailabilityChip = (availability: boolean) => {
    const config = availability 
      ? { color: '#39843c', icon: <CheckCircle sx={{ fontSize: 16, color: 'white' }} />, text: 'Available' }
      : { color: '#9e9e9e', icon: <Cancel sx={{ fontSize: 16, color: 'white' }} />, text: 'Unavailable' };
    
    return (
      <Chip
        icon={config.icon}
        label={config.text}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: 'white',
          fontWeight: 500,
          '& .MuiChip-icon': {
            color: 'white !important',
          },
        }}
      />
    );
  };

  const getAlertIcon = (alert: boolean) => {
    if (alert) {
      return (
        <Box
          component="img"
          src={process.env.PUBLIC_URL + "/BellIcon.svg"}
          alt="Alert"
          sx={{
            width: 20,
            height: 20,
            // Fargen skal bli lik custom.status.error
            filter: `brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)`,
          }}
        />
      );
    }
    return null;
  };


  const filteredChargers = chargers.filter(charger => {
    if (!charger) return false;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in multiple columns with safe property access
    const unitId = (charger.unit_name || '').toLowerCase();
    const model = (charger.model || '').toLowerCase();
    const vendor = (charger.vendor || '').toLowerCase();
    const availability = charger.availability ? 'available' : 'unavailable';
    const type = (charger.Type || '').toLowerCase();
    const maxPower = `${charger.max_power || 0} kw`;
    const voltage = `${charger.voltage || 0} v`;
    
    return (
      unitId.includes(searchLower) ||
      model.includes(searchLower) ||
      vendor.includes(searchLower) ||
      availability.includes(searchLower) ||
      type.includes(searchLower) ||
      maxPower.includes(searchLower) ||
      voltage.includes(searchLower)
    );
  });

  // Sort the filtered chargers
  const sortedChargers = [...filteredChargers].sort((a, b) => {
    if (!sortField) return 0;

    const asText = (value: unknown) => String(value ?? '').toLowerCase();
    const asNumber = (value: unknown) => {
      const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
      return Number.isFinite(parsed) ? parsed : null;
    };

    let aValue: string | number | null;
    let bValue: string | number | null;

    switch (sortField) {
      case 'unit_name':
        aValue = asText(a.unit_name);
        bValue = asText(b.unit_name);
        break;
      case 'model':
        aValue = asText(a.model);
        bValue = asText(b.model);
        break;
      case 'availability':
        aValue = a.availability ? 1 : 0;
        bValue = b.availability ? 1 : 0;
        break;
      case 'Type':
        aValue = asText(a.Type);
        bValue = asText(b.Type);
        break;
      case 'max_power':
        aValue = asNumber(a.max_power);
        bValue = asNumber(b.max_power);
        break;
      case 'voltage':
        aValue = asNumber(a.voltage);
        bValue = asNumber(b.voltage);
        break;
      default:
        return 0;
    }

    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return sortDirection === 'asc' ? 1 : -1;
    if (bValue === null) return sortDirection === 'asc' ? -1 : 1;
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChargerClick = (unitId: string) => {
    navigate(`/assets/chargers/${unitId}`);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddChargerClick = () => {
    setIsAddChargerModalOpen(true);
  };

  const handleCloseAddChargerModal = () => {
    setIsAddChargerModalOpen(false);
  };

  const getSortIcon = (field: string) => {
    if (sortField === field) {
      // Show the appropriate arrow for the current sort direction
      return (
        <Box
          component="img"
          src={sortDirection === 'asc' ? '/ChevronDownIcon.svg' : '/ChevronUpIcon.svg'}
          alt={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
          sx={{ width: 18, height: 18, ml: 1, verticalAlign: 'middle' }}
        />
      );
    }
    
    // Show a default chevron for hover state
    return (
      <Box
        component="img"
        src={process.env.PUBLIC_URL + "/ChevronDownIcon.svg"}
        alt="Sort"
        sx={{ width: 18, height: 18, ml: 1, opacity: 0.5, verticalAlign: 'middle' }}
      />
    );
  };

  // Empty state component
  if (chargers.length === 0 && !loading) {
    return (
      <Box sx={{ 
        px: 3, py: 0,
        backgroundColor: '#FAFAFA', 
        minHeight: '100vh',
        color: 'black'
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center', 
          mb: 4 
        }}>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                color: 'rgba(0, 0, 0, 0.87)',
                fontFamily: 'Montserrat',
                fontSize: '20px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '28px',
              }}
            >
              Chargers
            </Typography>
            {error && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.error.main,
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  mt: 0.5,
                }}
              >
                {errorCode ? `Error ${errorCode}` : 'API Error'}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Empty State */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          px: 3
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontFamily: 'Montserrat',
              fontSize: '24px',
              fontWeight: 500,
              mb: 2
            }}
          >
            No chargers found
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontFamily: 'Montserrat',
              fontSize: '16px',
              mb: 4,
              maxWidth: '400px'
            }}
          >
            You haven't added any chargers yet. Click the button below to request a new charger to be added to your organization.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddChargerClick}
            sx={{
              backgroundColor: (theme) => theme.palette.primary.light,
              color: 'white',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.primary.main,
                opacity: 0.9,
              },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              px: 4,
              py: 1.5,
            }}
          >
            ADD CHARGER
          </Button>
        </Box>

        {/* Add Charger Request Modal */}
        <AddChargerRequestModal
          open={isAddChargerModalOpen}
          onClose={handleCloseAddChargerModal}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      px: 3, py: 0, // 24px horizontal padding, no vertical padding
      backgroundColor: '#FAFAFA', 
      minHeight: '100vh',
      color: 'black'
    }}>
      {/* Header with Add Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Box>
          <Typography 
            variant="h3" 
            sx={{ 
              color: 'rgba(0, 0, 0, 0.87)',
              fontFamily: 'Montserrat',
              fontSize: '20px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '28px',
            }}
          >
            Chargers
          </Typography>
          {error && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.error.main,
                fontFamily: 'Montserrat',
                fontSize: '12px',
                mt: 0.5,
              }}
            >
              {errorCode ? `Error ${errorCode}` : 'API Error'}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddChargerClick}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.light,
            color: 'white',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.main,
              opacity: 0.9,
            },
          }}
        >
          ADD CHARGER
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '300px',
          textAlign: 'center'
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: theme.palette.text.secondary }}>
            Loading chargers...
          </Typography>
        </Box>
      ) : (
        <>
      {/* Search and Filter Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 4 
      }}>
        <TextField
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search sx={{ color: 'rgba(0, 0, 0, 0.6)' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: '220px',
            '& .MuiOutlinedInput-root': {
              height: '40px',
              padding: '0 12px',
              borderRadius: '4px',
              border: '1px solid rgba(0,0,0,0.23)',
              '&:hover': {
                border: '1px solid rgba(0,0,0,0.87)',
              },
              '&.Mui-focused': {
                border: '2px solid #337a5c',
              },
            },
            '& .MuiInputBase-input': {
              height: '40px',
              padding: '8px 0',
              color: 'rgba(0, 0, 0, 0.60)',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(0, 0, 0, 0.60)',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        />


      </Box>

      {/* Separator Line */}
      <Box sx={{ 
        height: '1px', 
        backgroundColor: '#ddd', 
        mb: 2
      }} />

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none'}}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('unit_name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Unit Name
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('unit_name')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('model')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Model
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('model')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('availability')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Availability
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('availability')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('Type')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Type
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('Type')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('max_power')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Max power
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('max_power')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('voltage')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Voltage
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('voltage')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 500, 
                borderBottom: '1px solid #616161',
                padding: '16px',
                fontFamily: 'Roboto',
                fontSize: '14px',
                color: 'rgba(0,0,0,0.87)',
                letterSpacing: '0.17px',
                lineHeight: '24px'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  24 hr SoC
                </Box>
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 500, 
                borderBottom: '1px solid #616161',
                padding: '16px',
                fontFamily: 'Roboto',
                fontSize: '14px',
                color: 'rgba(0,0,0,0.87)',
                letterSpacing: '0.17px',
                lineHeight: '24px'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Alert
                </Box>
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 500, 
                borderBottom: '1px solid #616161',
                padding: '16px',
                fontFamily: 'Roboto',
                fontSize: '14px',
                color: 'rgba(0,0,0,0.87)',
                letterSpacing: '0.17px',
                lineHeight: '24px',
                textAlign: 'right'
              }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedChargers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((charger) => (
              <TableRow key={charger.unit_id} hover sx={{ backgroundColor: '#FAFAFA', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Link 
                    component="button"
                    onClick={() => handleChargerClick(charger.unit_id)}
                    sx={{ 
                      color: '#337a5c', 
                      textDecoration: 'underline',
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      lineHeight: '1.43',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      padding: 0,
                    }}
                  >
                    {charger.unit_name}
                  </Link>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography sx={{ 
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      color: 'rgba(0,0,0,0.87)',
                      lineHeight: '1.43'
                    }}>
                      {charger.vendor}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ 
                        fontFamily: 'Montserrat',
                        fontSize: '12px',
                        color: 'rgba(0,0,0,0.6)',
                        lineHeight: '1.66',
                        letterSpacing: '0.4px'
                      }}>
                        {charger.model}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  {getAvailabilityChip(charger.availability)}
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ 
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      color: 'rgba(0,0,0,0.87)',
                      lineHeight: '1.43',
                      textTransform: 'capitalize'
                    }}>
                      {charger.Type}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Typography sx={{ 
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    color: 'rgba(0,0,0,0.87)',
                    lineHeight: '1.43'
                  }}>
                    {charger.max_power} kW
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Typography sx={{ 
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    color: 'rgba(0,0,0,0.87)',
                    lineHeight: '1.43'
                  }}>
                    {charger.voltage} V
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '8px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  {charger.soc && Array.isArray(charger.soc) && charger.soc.length > 0 ? (
                    <Box sx={{ 
                      width: '100%', 
                      height: 80, // Increased height for larger chart
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px 0'
                    }}>
                      <svg width="100%" height="64" viewBox="0 0 100 64">
                        {(() => {
                          try {
                            // Filter out invalid SOC entries and sort by timestamp
                            const validSoc = charger.soc.filter(soc => 
                              soc && 
                              typeof soc.value === 'number' && 
                              soc.timestamp && 
                              !isNaN(new Date(soc.timestamp).getTime())
                            );
                            
                            if (validSoc.length === 0) return null;
                            
                            // Sort SOC data by timestamp in ascending order (oldest to newest)
                            const sortedSoc = [...validSoc].sort((a, b) => {
                              return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
                            });
                            
                            // Use the sorted SOC data from the API
                            const data = sortedSoc.map(soc => soc.value);
                            const dataLength = data.length;
                            
                            if (dataLength === 0) return null;
                            
                            const points = data.map((value, index) => {
                              const x = dataLength > 1 ? (index / (dataLength - 1)) * 100 : 50;
                              const y = 64 - ((value / 100) * 64); // Scale to new height
                              return `${x},${y}`;
                            }).join(' ');
                          
                            // Create area path for fill
                            const areaPath = `M 0,64 L ${points} L 100,64 Z`;
                            
                            return (
                              <>
                                <path
                                  d={areaPath}
                                  fill="rgba(51, 122, 92, 0.08)"
                                />
                                <polyline
                                  points={points}
                                  fill="none"
                                  stroke="#4caf50"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                {/* Add invisible hover areas for tooltips */}
                                {sortedSoc.map((soc, index) => {
                                  const x = sortedSoc.length > 1 ? (index / (sortedSoc.length - 1)) * 100 : 50;
                                  const y = 64 - ((soc.value / 100) * 64);
                                  const timestamp = new Date(soc.timestamp);
                                  const timeString = timestamp.toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: false 
                                  });
                                  
                                  return (
                                    <Tooltip
                                      key={index}
                                      title={`SoC: ${soc.value.toFixed(1)}% ${timeString}`}
                                      placement="top"
                                      arrow
                                    >
                                      <circle
                                        cx={x}
                                        cy={y}
                                        r="8"
                                        fill="transparent"
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </Tooltip>
                                  );
                                })}
                              </>
                            );
                          } catch (error) {
                            console.error('Error rendering SOC chart:', error);
                            return null;
                          }
                        })()}
                      </svg>
                    </Box>
                  ) : null}
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  {getAlertIcon(charger.alert)}
                </TableCell>
                <TableCell sx={{ 
                  padding: '12px 16px', 
                  borderBottom: '1px solid rgba(0,0,0,0.12)',
                  textAlign: 'right'
                }}>
                  <IconButton size="small">
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={sortedChargers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ 
          borderTop: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 0,
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-select': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.3px',
            lineHeight: '19px'
          }
        }}
      />

      {/* Add Charger Request Modal */}
      <AddChargerRequestModal
        open={isAddChargerModalOpen}
        onClose={handleCloseAddChargerModal}
      />
        </>
      )}
    </Box>
  );
};

export default Chargers;
